import React, { useState, useEffect, useRef } from 'react';
import { Accessibility } from 'lucide-react';

const AccessibilityToggle = () => {
  const [isAccessibleMode, setIsAccessibleMode] = useState(false);
  const [mounted, setMounted] = useState(false);
  const announcerRef = useRef(null);

  // Initialize on mount - check localStorage and OS preference
  useEffect(() => {
    // Check localStorage first
    const storedPreference = localStorage.getItem('vett_a11y_mode');
    
    if (storedPreference !== null) {
      // Use stored preference
      setIsAccessibleMode(storedPreference === 'true');
    } else {
      // Check OS preference for reduced motion
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) {
        setIsAccessibleMode(true);
        localStorage.setItem('vett_a11y_mode', 'true');
      }
    }
    
    setMounted(true);
  }, []);

  // Apply/remove accessible mode class to document
  useEffect(() => {
    if (!mounted) return;
    
    if (isAccessibleMode) {
      document.documentElement.classList.add('a11y-mode');
    } else {
      document.documentElement.classList.remove('a11y-mode');
    }
  }, [isAccessibleMode, mounted]);

  // Listen for OS preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = (e) => {
      if (e.matches) {
        setIsAccessibleMode(true);
        localStorage.setItem('vett_a11y_mode', 'true');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleToggle = () => {
    const newMode = !isAccessibleMode;
    setIsAccessibleMode(newMode);
    localStorage.setItem('vett_a11y_mode', newMode.toString());
    
    // Announce to screen readers
    if (announcerRef.current) {
      announcerRef.current.textContent = newMode 
        ? 'Accessibility mode enabled. Animations disabled, focus rings enhanced.'
        : 'Accessibility mode disabled. Animations enabled.';
    }
  };

  // Don't render until mounted to prevent hydration mismatch
  if (!mounted) return null;

  return (
    <>
      {/* Screen reader announcer */}
      <div
        ref={announcerRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />
      
      {/* Toggle button */}
      <button
        onClick={handleToggle}
        aria-pressed={isAccessibleMode}
        aria-label="Toggle accessibility mode"
        data-testid="a11y-toggle-btn"
        className={`
          fixed bottom-6 right-6 z-[9999]
          w-12 h-12 min-w-[44px] min-h-[44px]
          rounded-full
          flex items-center justify-center
          transition-all duration-200
          ${isAccessibleMode 
            ? 'bg-[#E94560] text-white shadow-lg shadow-[#E94560]/30' 
            : 'bg-[#1A1A2E] text-[#8888AA] border border-[#2A2A4A] hover:border-[#E94560] hover:text-[#E94560]'
          }
          focus:outline-none focus:ring-[3px] focus:ring-white focus:shadow-[0_0_0_6px_rgba(240,84,95,0.4)]
        `}
        title={isAccessibleMode ? 'Accessibility mode: ON' : 'Accessibility mode: OFF'}
      >
        <Accessibility className="w-5 h-5" />
      </button>
    </>
  );
};

export default AccessibilityToggle;
